import React, { useState, useMemo } from 'react';
import Form from '../../components/common/form/Form';
import { PageHeaderWithBackButton } from '../../components/common/PageHeaderWithBackButton';
import style from './style.module.css';
import * as Yup from 'yup';
import {
  useCreateEditPaymentMode,
  PaymentModeFormType,
  PaymentModeType,
  useFetchPaymentModeList,
  useFetchCompanyPaymentModeList,
  CreateEditPaymentModePayload,
  useFetchOutletsPaymentMode,
  useFetchFavePaymentModeList,
} from '../../services/PaymentMode/usePaymentMode';
import { calcTableHeight } from '../../utils/utilFunctions';
import { Message } from 'fave-ui';
import { Spin } from 'antd';
import { useLocation, useParams } from 'react-router';
import { toolRedirect } from '../../services/http';
import PaymentModeCreateForm from './PaymentModeCreateForm';
import {
  approvalDocumentsFieldKey,
  companyPaymentModesFieldKey,
  groupByPaymentMode,
  groupByPaymentModeSchema,
  favePaymentModesFieldKey,
} from './helpers/paymentModeHelpers';
import { FormikHelpers } from 'formik';
import { useSearchParams } from 'react-router-dom';
import { ActionForm, OutletStatusPaymentMode } from './helpers/enums';

const tableHeight = calcTableHeight(225);

const onSuccessSubmitForm = (merchantID: number, isEditMode: boolean) =>
  Message.success({
    content: `Successfully ${isEditMode ? 'edited' : 'created'} Payment Mode.`,
  }).then(() =>
    toolRedirect(`/admin/merchant_connect/merchants/${merchantID}/favepay`),
  );

const onSubmitForm = (
  values: PaymentModeFormType,
  merchantID: number,
  isFastpay: boolean,
  createEditPaymentMode: (
    data: CreateEditPaymentModePayload,
    response: { onSuccess: () => void; onError: () => void },
  ) => void,
  isEditMode: boolean,
  { setSubmitting }: FormikHelpers<PaymentModeFormType>,
  listOutletsDefault: string[],
) => {
  const formData = new FormData();
  if (isFastpay) {
    delete values.outlets;
  }

  Object.keys(values).forEach(value => {
    if (value === approvalDocumentsFieldKey) {
      values.approval_documents?.forEach(
        file =>
          file.originFileObj &&
          formData.append(
            `${approvalDocumentsFieldKey}[]`,
            file.originFileObj as Blob,
          ),
      );
    } else {
      values[value]
        .filter(paymentMode => {
          const key = `rate${paymentMode.group?.replace(' ', '')}${
            paymentMode.variant
          }`;
          return (
            paymentMode.is_ipp ||
            paymentMode.is_atome ||
            paymentMode.is_shopee_pay ||
            (paymentMode[key] && (paymentMode[key] as number) > 0)
          );
        })
        .forEach(item => {
          // payment mode handling for fast merchants
          if (isFastpay) {
            formData.append(
              `${companyPaymentModesFieldKey}[][status]`,
              item.status ? 'enabled' : 'disabled',
            );
            formData.append(
              `${companyPaymentModesFieldKey}[][rate]`,
              item[
                `rate${item.group.replace(' ', '')}${item.variant}`
              ] as unknown as string,
            );

            if (item.is_fpx) {
              formData.append(
                `${companyPaymentModesFieldKey}[][is_fpx]`,
                item.is_fpx as unknown as string,
              );
              formData.append(
                `${companyPaymentModesFieldKey}[][fixed_amount]`,
                item[
                  `fixed_amount${item.group.replace(' ', '')}${item.variant}`
                ] as unknown as string,
              );
            } else {
              if (isEditMode) {
                const isNewPaymentMode = !!!item.payment_mode?.id;

                // new payment mode that was never selected before
                // does not have payment mode id in db
                if (isNewPaymentMode) {
                  formData.append(
                    `${companyPaymentModesFieldKey}[][payment_mode_id]`,
                    item.id as unknown as string,
                  );
                } else {
                  formData.append(
                    `${companyPaymentModesFieldKey}[][id]`,
                    item.id as unknown as string,
                  );
                  formData.append(
                    `${companyPaymentModesFieldKey}[][payment_mode_id]`,
                    item.payment_mode.id as unknown as string,
                  );
                }
              } else {
                formData.append(
                  `${companyPaymentModesFieldKey}[][payment_mode_id]`,
                  item.id as unknown as string,
                );
              }
            }
          }
          // payment mode handling for fave merchants
          else {
            if (
              item.status &&
              !item.is_ipp &&
              !item.is_gpl &&
              !item.is_atome &&
              !item.is_shopee_pay
            ) {
              if (isEditMode) {
                formData.append(
                  `${favePaymentModesFieldKey}[][id]`,
                  item.id as unknown as string,
                );
                // if payment mode is new, it does not have payment mode id
                const paymentModeId = !!item.payment_mode
                  ? item.payment_mode.id
                  : item.id;
                formData.append(
                  `${favePaymentModesFieldKey}[][payment_mode_id]`,
                  paymentModeId as unknown as string,
                );
              } else {
                const paymentModeId = item.id || item.payment_mode?.id;
                formData.append(
                  `${favePaymentModesFieldKey}[][payment_mode_id]`,
                  paymentModeId as unknown as string,
                );
              }
              formData.append(
                `${favePaymentModesFieldKey}[][rate]`,
                (item[
                  `rate${item.group.replace(' ', '')}${item.variant}`
                ] as unknown as string) || '',
              );
              if (
                item.is_fpx &&
                item[
                  `fixed_amount${item.group.replace(' ', '')}${item.variant}`
                ]
              ) {
                formData.append(
                  `${favePaymentModesFieldKey}[][is_fpx]`,
                  item.is_fpx as unknown as string,
                );
                formData.append(
                  `${favePaymentModesFieldKey}[][fixed_amount]`,
                  item[
                    `fixed_amount${item.group.replace(' ', '')}${item.variant}`
                  ] as unknown as string,
                );
              }
            }

            // for ipp
            if (item.is_ipp) {
              const isNewIppAdded = !!!item.payment_mode?.id;
              const inputRate =
                item[`rate${item.group.replace(' ', '')}${item.variant}`];
              // allow ipp rate to submit as 0 to indicate opt out
              const formRate = inputRate === '' ? 0 : inputRate;

              if (isNewIppAdded)
                formData.append(
                  `${favePaymentModesFieldKey}[][payment_mode_id]`,
                  item.id as unknown as string,
                );
              else
                formData.append(
                  `${favePaymentModesFieldKey}[][payment_mode_id]`,
                  item.payment_mode.id as unknown as string,
                );

              formData.append(
                `${favePaymentModesFieldKey}[][rate]`,
                formRate as unknown as string,
              );
            }

            // for gpl
            if (item.is_gpl) {
              // if payment mode is new, it does not have payment_mode
              // this includes new submission
              formData.append(
                `${favePaymentModesFieldKey}[][payment_mode_id]`,
                (item.payment_mode
                  ? item.payment_mode.id
                  : item.id) as unknown as string,
              );
              formData.append(
                `${favePaymentModesFieldKey}[][is_gpl]`,
                item.is_gpl as unknown as string,
              );
              formData.append(
                `${favePaymentModesFieldKey}[][rate]`,
                // keys must match name used in inputfield
                String(
                  item[`rate${item.group.replace(' ', '')}${item.variant}`] ||
                    0,
                ),
              );

              const minimumSpendAmountValue = String(
                item[
                  `minimum_spend_amount${item.group.replace(' ', '')}${item.variant}`
                ] || 0,
              );

              formData.append(
                `${favePaymentModesFieldKey}[][minimum_spend_amount]`,
                // keys must match name used in inputfield
                minimumSpendAmountValue,
              );

              if (item.is_gpl_via_pos) {
                formData.append(
                  `${favePaymentModesFieldKey}[][store_grab_id]`,
                  String(
                    item[
                      `store_grab_id${item.group.replace(' ', '')}${item.variant}`
                    ] || '',
                  ),
                );
                formData.append(
                  `${favePaymentModesFieldKey}[][terminal_id]`,
                  String(
                    item[
                      `terminal_id${item.group.replace(' ', '')}${item.variant}`
                    ] || '',
                  ),
                );
                formData.append(
                  `${favePaymentModesFieldKey}[][is_gpl_via_pos]`,
                  item.is_gpl_via_pos as unknown as string,
                );
              } else {
                formData.append(
                  `${favePaymentModesFieldKey}[][is_cashback_opt_out]`,
                  // keys must match name used in inputfield
                  !!item[
                    `is_cashback_opt_out${item.group.replace(' ', '')}${item.variant}`
                  ] as unknown as string,
                );
              }
            }

            // for atome
            if (item.is_atome) {
              const atomeRate =
                item[`rate${item.group.replace(' ', '')}${item.variant}`];
              const hasRate =
                atomeRate !== undefined &&
                atomeRate !== null &&
                atomeRate !== '' &&
                atomeRate !== 0 &&
                atomeRate !== '0';

              if (hasRate) {
                formData.append(
                  `${favePaymentModesFieldKey}[][payment_mode_id]`,
                  (item.payment_mode
                    ? item.payment_mode.id
                    : item.id) as unknown as string,
                );
                formData.append(
                  `${favePaymentModesFieldKey}[][is_atome]`,
                  item.is_atome as unknown as string,
                );
                formData.append(
                  `${favePaymentModesFieldKey}[][rate]`,
                  String(atomeRate),
                );
                formData.append(
                  `${favePaymentModesFieldKey}[][username]`,
                  String(
                    item[
                      `username${item.group.replace(' ', '')}${item.variant}`
                    ] || '',
                  ),
                );
                formData.append(
                  `${favePaymentModesFieldKey}[][password]`,
                  String(
                    item[
                      `password${item.group.replace(' ', '')}${item.variant}`
                    ] || '',
                  ),
                );
              } else if (isEditMode) {
                // rate cleared → add to removed list so BE deletes the record
                const paymentModeId = item.payment_mode?.id || item.id;
                if (paymentModeId) {
                  const removedIds = values.removed_payment_mode_ids || [];
                  if (!removedIds.includes(paymentModeId as number)) {
                    values.removed_payment_mode_ids = [
                      ...removedIds,
                      paymentModeId as number,
                    ];
                  }
                }
              }
            }

            // for shopee pay
            if (item.is_shopee_pay) {
              const shopeePayRate =
                item[`rate${item.group.replace(' ', '')}${item.variant}`];
              const hasRate =
                shopeePayRate !== undefined &&
                shopeePayRate !== null &&
                shopeePayRate !== '' &&
                shopeePayRate !== 0 &&
                shopeePayRate !== '0';

              if (hasRate) {
                formData.append(
                  `${favePaymentModesFieldKey}[][payment_mode_id]`,
                  (item.payment_mode
                    ? item.payment_mode.id
                    : item.id) as unknown as string,
                );
                formData.append(
                  `${favePaymentModesFieldKey}[][is_shopee_pay]`,
                  item.is_shopee_pay as unknown as string,
                );
                formData.append(
                  `${favePaymentModesFieldKey}[][rate]`,
                  String(shopeePayRate),
                );
                formData.append(
                  `${favePaymentModesFieldKey}[][merchant_id]`,
                  String(
                    item[
                      `merchant_id${item.group.replace(' ', '')}${item.variant}`
                    ] || '',
                  ),
                );
                formData.append(
                  `${favePaymentModesFieldKey}[][store_id]`,
                  String(
                    item[
                      `store_id${item.group.replace(' ', '')}${item.variant}`
                    ] || '',
                  ),
                );
              } else if (isEditMode) {
                // rate cleared → add to removed list so BE deletes the record
                const paymentModeId = item.payment_mode?.id || item.id;
                if (paymentModeId) {
                  const removedIds = values.removed_payment_mode_ids || [];
                  if (!removedIds.includes(paymentModeId as number)) {
                    values.removed_payment_mode_ids = [
                      ...removedIds,
                      paymentModeId as number,
                    ];
                  }
                }
              }
            }
          }
        });
    }
  });

  if (!isFastpay && values.outlets?.length) {
    values.outlets?.forEach(outlet => {
      formData.append('outlet_ids[]', outlet as unknown as string);
    });
    listOutletsDefault
      ?.filter(outlet => !values.outlets?.includes(outlet))
      .forEach(outlet => {
        formData.append('removed_outlet_ids[]', outlet as unknown as string);
      });
  }

  if (
    !isFastpay &&
    isEditMode &&
    values.removed_payment_mode_ids &&
    values.removed_payment_mode_ids.length
  ) {
    Array.from(new Set(values.removed_payment_mode_ids)).forEach(id =>
      formData.append('removed_payment_mode_ids[]', String(id)),
    );
  }

  createEditPaymentMode(
    { company_id: merchantID, params: formData },
    {
      onSuccess: () => {
        onSuccessSubmitForm(merchantID, isEditMode);
        setSubmitting(false);
      },
      onError: () => setSubmitting(false),
    },
  );
};

const PaymentModeConfigurationFormView: React.FC = () => {
  const [statusOutletPaymentMode, setStatusOutletPaymentMode] = useState(
    OutletStatusPaymentMode.New,
  );

  const { id, mode } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useLocation();
  const params = new URLSearchParams(navigate.search);
  const merchantID = parseInt(id || '');
  const { data: paymentModeListData, isFetching: isPaymentModeListFetching } =
    useFetchPaymentModeList({ merchant_id: merchantID });

  const isEditMode = mode === ActionForm.Edit;
  const isCreateMode = mode === ActionForm.Create;
  const isResubmitMode = mode === ActionForm.Resubmit;
  const isResubmit = searchParams.get('resubmit') === 'true';
  const onboardingStatus = isResubmit ? 'unsigned,cancelled' : 'draft';
  const isFastpay = params.get('merchant_type') === 'fastpay';

  const listOutletsIdEdited = params.get('outlet_ids')?.split(',') || [];
  // should only exist when status is unsigned, cancelled
  const mcContractSubmissionIds =
    params.get('mc_contract_submission_ids')?.split(',') || [];

  const hasOutletsIdsInQueryString = listOutletsIdEdited.length > 0;
  const hasMcContractSubmissionIdsInQueryString =
    mcContractSubmissionIds.length > 0;

  const outletsIdFilter = hasOutletsIdsInQueryString
    ? `|outlet_ids=${listOutletsIdEdited.join(',')}`
    : '';
  const mcContractSubmissionIdFilter = hasMcContractSubmissionIdsInQueryString
    ? `|mc_contract_submission_ids=${mcContractSubmissionIds.join(',')}`
    : '';

  const {
    data: fetchCompanyPaymentModeListData,
    isFetching: isFetchCompanyModeListDataFetching,
  } = useFetchCompanyPaymentModeList({
    company_id: merchantID,
    filter: `onboarding_status=${onboardingStatus}`,
    enable: isFastpay,
  });

  const {
    data: fetchPaymentModeListDataForFave,
    isFetching: isFetchModeListFaveDataFetching,
  } = useFetchFavePaymentModeList({
    company_id: merchantID,
    enable: !isFastpay && !isResubmitMode,
    filter: `onboarding_status=${onboardingStatus}${outletsIdFilter}${mcContractSubmissionIdFilter}`,
  });

  const { data: fetchListOutletPaymentModeData } = useFetchOutletsPaymentMode({
    company_id: merchantID,
    isFastpay,
  });
  const listOutlets = useMemo(
    () =>
      fetchListOutletPaymentModeData?.map(outlet => ({
        key: `${outlet.id}`,
        label: outlet.name,
        value: `${outlet.id}`,
        ...outlet,
      })) ?? [],
    [fetchListOutletPaymentModeData],
  );

  const { mutate: createEditPaymentMode } = useCreateEditPaymentMode({
    isUpdate: isEditMode,
    isFastpay: isFastpay,
  });

  const newCompanyPaymentModeListData = isFastpay
    ? (paymentModeListData?.payment_modes?.map(existingPaymentMode => {
        const findMatch =
          fetchCompanyPaymentModeListData?.mc_company_payment_modes.find(
            currentCompanyPaymentMode => {
              return (
                currentCompanyPaymentMode.payment_mode.id ===
                existingPaymentMode.id
              );
            },
          );

        if (findMatch)
          // insert current company's selected payment modes
          // spread findMatch.payment_mode so that groupByPaymentMode can read object
          return {
            ...findMatch,
            group: findMatch.payment_mode.group,
            variant: findMatch.payment_mode.variant,
            is_ipp: findMatch.payment_mode.is_ipp,
          };
        else return existingPaymentMode;
      }) as PaymentModeType[])
    : (paymentModeListData?.payment_modes?.map(existingPaymentMode => {
        const findMatch =
          fetchPaymentModeListDataForFave?.mc_outlet_payment_modes.find(
            currentOutletsPaymentMode => {
              return (
                currentOutletsPaymentMode.payment_mode.id ===
                existingPaymentMode.id
              );
            },
          );

        if (findMatch)
          // insert current outlets' selected payment modes
          // spread findMatch.payment_mode so that groupByPaymentMode can read object
          return {
            ...findMatch,
            group: findMatch.payment_mode.group,
            variant: findMatch.payment_mode.variant,
            is_ipp: findMatch.payment_mode.is_ipp,
          };
        else return existingPaymentMode;
      }) as PaymentModeType[]);

  const hasPaymentMode =
    isEditMode && isFastpay
      ? fetchCompanyPaymentModeListData?.mc_company_payment_modes &&
        fetchCompanyPaymentModeListData?.mc_company_payment_modes.length > 0
      : isCreateMode
        ? false
        : // For fave mode, list payment mode could be existed while in create mode
          fetchPaymentModeListDataForFave?.mc_outlet_payment_modes &&
          fetchPaymentModeListDataForFave?.mc_outlet_payment_modes.length > 0;

  const groupedPaymentModes = groupByPaymentMode(
    hasPaymentMode
      ? newCompanyPaymentModeListData
      : (paymentModeListData?.payment_modes as PaymentModeType[]),
    isFastpay,
  );

  const initialOutlets = listOutlets
    .filter(outlet => listOutletsIdEdited.includes(`${outlet.id}`))
    .map(outlet => outlet.value);

  const initialValues = {
    ...groupedPaymentModes,
    approval_documents:
      isEditMode || isResubmitMode
        ? isFastpay
          ? fetchCompanyPaymentModeListData?.approval_documents
          : fetchPaymentModeListDataForFave?.approval_documents
        : undefined,
    outlets: isCreateMode ? [] : initialOutlets,
  } as unknown as PaymentModeFormType;

  const dynamicSchema =
    groupedPaymentModes &&
    groupByPaymentModeSchema(groupedPaymentModes, statusOutletPaymentMode);

  const schema = {
    ...dynamicSchema,
    approval_documents: Yup.mixed().required('Please upload at least 1 file.'),
  };

  const formSchema = useMemo(() => {
    return isFastpay
      ? Yup.object().shape(schema)
      : Yup.object().shape({
          ...schema,
          approval_documents:
            isCreateMode || isResubmitMode
              ? Yup.mixed().optional()
              : Yup.mixed().required('Please upload at least 1 file.'),
          outlets: Yup.array()
            .of(Yup.string())
            .min(1, 'Please select at least 1 outlet')
            .required('Please select at least 1 outlet'),
        });
  }, [schema, isCreateMode, isResubmitMode]);

  return (
    <div className={style.paymentModeView}>
      <PageHeaderWithBackButton
        title={'Payment Mode'}
        subTitle={'Please select at least one payment mode'}
      />
      <div className={style.content}>
        <div className={style.form} style={{ height: tableHeight }}>
          <p className={style.formTitle}>Select Mode</p>

          <Spin
            spinning={
              isEditMode || isResubmitMode
                ? isFetchCompanyModeListDataFetching ||
                  isFetchModeListFaveDataFetching
                : isPaymentModeListFetching
            }
          >
            <Form
              initialValues={initialValues}
              onSubmit={(
                values: PaymentModeFormType,
                helpers: FormikHelpers<PaymentModeFormType>,
              ) =>
                onSubmitForm(
                  values,
                  merchantID,
                  isFastpay,
                  createEditPaymentMode,
                  isEditMode,
                  helpers,
                  initialOutlets,
                )
              }
              validationSchema={formSchema}
              validateOnMount
              validateOnChange
              validateOnSchemaChange
            >
              <PaymentModeCreateForm
                listOutlets={listOutlets}
                setStatusOutletPaymentMode={setStatusOutletPaymentMode}
              />
            </Form>
          </Spin>
        </div>
      </div>
    </div>
  );
};

export default PaymentModeConfigurationFormView;
